/**
 * SendSmsButton - Reusable button + dialog to send ad-hoc SMS from any module record.
 *
 * Usage:
 *   <SendSmsButton module="projects" entityId={project.project_id} defaultRecipients={["+420..."]}>
 *     <PaperPlane /> Poslat SMS
 *   </SendSmsButton>
 */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquare, Send, Loader2, Eye } from "lucide-react";
import { BACKEND_URL, API_V1} from "../config/api";
import ModalShell from "./ModalShell";
import { AppSelect } from "./AppSelect";

const API = (BACKEND_URL || "") + "/api/v1";

export default function SendSmsButton({ module, entityId, defaultRecipients = [], label = "SMS", iconOnly = false, className = "", ...rest }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button onClick={() => setOpen(true)}
        className={className || "px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-xs hover:bg-blue-100 flex items-center gap-1"}
        title="Poslat SMS"
        data-testid={`send-sms-btn-${entityId || module}`}
        {...rest}>
        <MessageSquare className="w-3 h-3" />
        {!iconOnly && <span>{label}</span>}
      </button>
      {open && (
        <SendSmsDialog
          module={module}
          entityId={entityId}
          defaultRecipients={defaultRecipients}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

export function SendSmsDialog({ module, entityId, defaultRecipients = [], onClose }) {
  const [templates, setTemplates] = useState([]);
  const [variables, setVariables] = useState({ system: [], module: [] });
  const [recipientsData, setRecipientsData] = useState({ participants: [], owner: null, creator: null, customer: null });
  const [picked, setPicked] = useState({
    participants: false, owner: false, creator: false, customer: false,
  });
  const [form, setForm] = useState({
    recipients_text: defaultRecipients.join("\n"),
    template_id: "",
    body: "",
    use_template: true,
  });
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Load templates filtered by module
  useEffect(() => {
    axios.get(`${API_V1}/sms/templates?module=${module || "custom"}`).then(r => {
      const list = r.data || [];
      setTemplates(list);
      const def = list.find(t => t.is_default);
      if (def) setForm(f => ({ ...f, template_id: def.template_id }));
      else if (list.length > 0) setForm(f => ({ ...f, template_id: list[0].template_id }));
    });
    axios.get(`${API_V1}/sms/meta/variables?module=${module || ""}`).then(r => setVariables(r.data || { system: [], module: [] }));
    if (module && entityId) {
      axios.get(`${API_V1}/sms/recipients/${module}/${entityId}`)
        .then(r => setRecipientsData(r.data || { participants: [], owner: null, creator: null, customer: null }))
        .catch(() => {});
    }
  }, [module, entityId]);

  // When picker checkboxes change, regenerate the recipients text
  useEffect(() => {
    const phones = new Set();
    if (picked.participants) {
      recipientsData.participants.forEach(p => p.phone && phones.add(p.phone));
    }
    if (picked.owner && recipientsData.owner?.phone) phones.add(recipientsData.owner.phone);
    if (picked.creator && recipientsData.creator?.phone) phones.add(recipientsData.creator.phone);
    if (picked.customer && recipientsData.customer?.phone) phones.add(recipientsData.customer.phone);
    const fromPicker = Array.from(phones);
    if (fromPicker.length > 0) {
      setForm(f => ({ ...f, recipients_text: fromPicker.join("\n") }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picked, recipientsData]);

  const insertVar = (k) => setForm(f => ({ ...f, body: f.body + ` {${k}}`, use_template: false }));

  const handlePreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const payload = form.use_template && form.template_id
        ? { template_id: form.template_id, module, entity_id: entityId }
        : { body: form.body, module, entity_id: entityId };
      const r = await axios.post(`${API_V1}/sms/preview`, payload);
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Chyba náhledu");
    }
    setPreviewLoading(false);
  }, [form, module, entityId]);

  const handleSend = async () => {
    const recipients = form.recipients_text.split(/[\n,]/).map(s => s.trim()).filter(Boolean);
    if (recipients.length === 0) { toast.error("Zadejte příjemce"); return; }
    if (!form.use_template && !form.body.trim()) { toast.error("Zadejte text SMS"); return; }
    if (form.use_template && !form.template_id) { toast.error("Vyberte šablonu"); return; }
    setSending(true);
    try {
      const payload = { recipients, module, entity_id: entityId };
      if (form.use_template) payload.template_id = form.template_id;
      else payload.body = form.body;
      await axios.post(`${API_V1}/sms/send`, payload);
      toast.success(`SMS odeslána (${recipients.length})`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Chyba odeslání");
    }
    setSending(false);
  };

  // Compute available picker options (only show if data is present)
  const pickerOptions = [];
  if (recipientsData.participants?.length > 0) {
    const withPhone = recipientsData.participants.filter(p => p.phone);
    pickerOptions.push({
      key: "participants",
      label: `Účastníci (${withPhone.length}/${recipientsData.participants.length})`,
      detail: withPhone.length === 0 ? "Žádný účastník nemá vyplněný telefon" : withPhone.map(p => `${p.name}: ${p.phone}`).join(", "),
      disabled: withPhone.length === 0,
    });
  }
  if (recipientsData.owner) {
    pickerOptions.push({
      key: "owner",
      label: `Vlastník: ${recipientsData.owner.name}`,
      detail: recipientsData.owner.phone || "Bez telefonu",
      disabled: !recipientsData.owner.phone,
    });
  }
  if (recipientsData.creator && recipientsData.creator.user_id !== recipientsData.owner?.user_id) {
    pickerOptions.push({
      key: "creator",
      label: `Tvůrce: ${recipientsData.creator.name}`,
      detail: recipientsData.creator.phone || "Bez telefonu",
      disabled: !recipientsData.creator.phone,
    });
  }
  if (recipientsData.customer) {
    pickerOptions.push({
      key: "customer",
      label: `Zákazník: ${recipientsData.customer.name || "(bez jména)"}`,
      detail: recipientsData.customer.phone || "Bez telefonu",
      disabled: !recipientsData.customer.phone,
    });
  }

  return (
    <ModalShell title="Poslat SMS" onClose={onClose}
      testId="send-sms-modal"
      footer={
        <div className="flex justify-end gap-2 w-full">
          <button onClick={handlePreview} disabled={previewLoading}
            className="px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm flex items-center gap-1"
            data-testid="preview-send-sms">
            {previewLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} Náhled
          </button>
          <button onClick={onClose} className="px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm">Zrušit</button>
          <button onClick={handleSend} disabled={sending}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
            data-testid="confirm-send-sms">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Odeslat
          </button>
        </div>
      }>
      <div className="space-y-3">
        {pickerOptions.length > 0 && (
          <div className="bg-blue-50/50 border border-blue-100 rounded-lg p-3" data-testid="recipient-picker">
            <div className="text-xs font-semibold text-slate-700 mb-2">Komu poslat? (rychlá volba)</div>
            <div className="space-y-1.5">
              {pickerOptions.map(opt => (
                <label key={opt.key}
                  className={`flex items-start gap-2 p-2 rounded-lg cursor-pointer transition-colors ${opt.disabled ? "opacity-40 cursor-not-allowed" : "hover:bg-white"}`}
                  data-testid={`picker-${opt.key}`}>
                  <input type="checkbox" disabled={opt.disabled}
                    checked={picked[opt.key] || false}
                    onChange={e => setPicked(p => ({ ...p, [opt.key]: e.target.checked }))}
                    className="mt-0.5 w-4 h-4 rounded cursor-pointer" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800">{opt.label}</div>
                    <div className="text-[11px] text-slate-500 truncate">{opt.detail}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        <div>
          <label className="text-xs font-semibold text-slate-600">
            Příjemci (telefonní čísla)
            <span className="text-[10px] text-slate-400 font-normal ml-1">— ručně doplň nebo uprav</span>
          </label>
          <textarea value={form.recipients_text}
            onChange={e => setForm({ ...form, recipients_text: e.target.value })}
            rows={2}
            placeholder="+420777111222"
            className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono"
            data-testid="send-sms-recipients" />
        </div>

        <div className="flex gap-1 bg-slate-50 rounded-lg p-1 text-xs">
          <button onClick={() => setForm({ ...form, use_template: true })}
            className={`flex-1 py-1.5 rounded-md transition-colors ${form.use_template ? "bg-white shadow-sm font-medium" : "text-slate-500"}`}
            data-testid="tab-use-template">Šablona</button>
          <button onClick={() => setForm({ ...form, use_template: false })}
            className={`flex-1 py-1.5 rounded-md transition-colors ${!form.use_template ? "bg-white shadow-sm font-medium" : "text-slate-500"}`}
            data-testid="tab-use-custom">Vlastní text</button>
        </div>

        {form.use_template ? (
          <div>
            <label className="text-xs font-semibold text-slate-600">Šablona</label>
            {templates.length === 0 ? (
              <p className="text-sm text-slate-400 mt-1">Pro modul "{module}" nejsou žádné šablony. <a href="/admin/sms" className="text-blue-600 underline">Vytvořit šablonu</a></p>
            ) : (
              <AppSelect value={form.template_id} onChange={v => setForm({ ...form, template_id: v })}
                options={templates.map(t => ({ value: t.template_id, label: `${t.name}${t.is_default ? " (výchozí)" : ""}` }))}
                data-testid="send-sms-template-select" />
            )}
            {form.template_id && (
              <div className="mt-2 p-2.5 bg-slate-50 rounded-lg text-xs text-slate-600 font-mono whitespace-pre-wrap">
                {templates.find(t => t.template_id === form.template_id)?.body}
              </div>
            )}
          </div>
        ) : (
          <div>
            <label className="text-xs font-semibold text-slate-600">Text SMS</label>
            <textarea value={form.body} onChange={e => setForm({ ...form, body: e.target.value })}
              rows={4}
              className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono"
              placeholder="Použij {variable} pro dynamické hodnoty"
              data-testid="send-sms-body" />
            <div className="text-[10px] text-slate-400 mt-1">{form.body.length} znaků ({Math.max(1, Math.ceil(form.body.length / 160))} SMS)</div>
            <div className="flex flex-wrap gap-1 mt-1">
              {[...variables.system, ...variables.module].map(v => (
                <button key={v.key} type="button" onClick={() => insertVar(v.key)}
                  className="text-[11px] px-2 py-0.5 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded font-mono"
                  title={v.label}>{`{${v.key}}`}</button>
              ))}
            </div>
          </div>
        )}

        {preview && (
          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs">
            <div className="font-semibold text-emerald-800 mb-1">Náhled vykresleného textu:</div>
            <div className="font-mono text-emerald-900 whitespace-pre-wrap">{preview.rendered_body}</div>
            {preview.preview?.stats && (
              <div className="mt-2 text-emerald-700">
                Cena: {preview.preview.stats.price} {preview.preview.stats.currency} •
                {" "}{preview.preview.stats.smsCount} SMS{preview.preview.stats.hasDiacritics ? " s diakritikou" : " bez diakritiky"}
              </div>
            )}
          </div>
        )}
      </div>
    </ModalShell>
  );
}
